import classnames from 'classnames/bind';
import React from 'react';
import { CellProps } from 'react-table';

import Pipeline from 'types/pipeline';
import { Stream } from 'types/stream';
import { Gateway } from 'types/gateway';

import {
  AttributeList,
  AttributeListItem,
} from 'components/AttributeList/AttributeList';
import { Icon } from 'components/Icon/IconDeprecated';
import { NameCell } from 'components/Table/Table';
import { TextAppLink } from 'components/TextAppLink/TextAppLink';
import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';

import styles from './Table.module.scss';

const c = classnames.bind(styles);

export type StreamsTableInfoCellProps = CellProps<Stream> & {
  isRenaming?: boolean;
  gateway?: Gateway;
  pipeline?: Pipeline;
  onCancelRename?: () => void;
  onRename?: (stream: Stream) => void;
  to?: string;
};

export function StreamsTableInfoCell({
  isRenaming: initialIsRenaming,
  gateway,
  pipeline,
  onCancelRename,
  onRename,
  to,
  ...props
}: StreamsTableInfoCellProps) {
  const stream = props.row.original;

  const [isRenaming, setIsRenaming] = React.useState(initialIsRenaming);

  React.useEffect(() => {
    setIsRenaming(initialIsRenaming);
  }, [initialIsRenaming]);

  function handleNameChange(newName: string) {
    const updatedStream = structuredClone(stream);
    updatedStream.name = newName;
    onRename?.(updatedStream);
    setIsRenaming(false);
  }

  const isGlobalStream =
    stream.gateway_id === undefined || stream.gateway_id === null;

  return (
    <div className={c('stream', stream.stream_type)}>
      <VideoSourceSnapshot
        className={c('stream-thumbnail')}
        source={stream}
        compact
        hideTitle
        autoRefresh={false}
      />

      <div className={c('stream-summary')}>
        <NameCell
          {...props}
          to={to}
          isRenaming={isRenaming}
          onNameChange={onRename ? handleNameChange : undefined}
          onCancel={onCancelRename}
          entity="stream"
        />

        <AttributeList className={c('stream-sources')}>
          {pipeline && pipeline.hasId() && (
            <AttributeListItem icon="pipeline" label="Workflow">
              {pipeline.id ? (
                <TextAppLink to={`/design/pipelines/${pipeline.id}`}>
                  {pipeline.name}
                </TextAppLink>
              ) : (
                <>unknown</>
              )}
            </AttributeListItem>
          )}
          {!gateway && isGlobalStream && (
            <span className={c('stream-type')}>
              <Icon name="global" size="small" />
              <span>Global</span>
            </span>
          )}
        </AttributeList>
      </div>
    </div>
  );
}
